<script setup lang="ts">
import { getArticleList } from "@carnet/api/backoffice";

const { t } = useI18n();
const ULink = resolveComponent("ULink");
const { loggedIn } = useUserSession();
const route = useRoute();
const show = computed(() => !loggedIn.value || route.meta.layout === "auth");
const nuxtApp = useNuxtApp();
const cachedData = { getCachedData: (key: string) => nuxtApp.payload.data[key] ?? nuxtApp.static.data[key] };

// L'API CMS peut renvoyer autre chose qu'un tableau (spec divergente) : on borne au tableau
// pour ne pas casser les `.find`/`.map`/`.filter` ci-dessous.
const fetchList = (context: string) =>
  getArticleList({ composable: "$fetch", path: { locale: appLocale() }, query: { context } }).then((r) => (Array.isArray(r) ? r : []));

const [{ data: singletons }, { data: logosData }, { data: socialsData }] = await Promise.all([
  useAsyncData("footer-singletons", () => fetchList("footer"), cachedData),
  useAsyncData("footer-logos", () => fetchList("footerLogos"), cachedData),
  useAsyncData("footer-socials", () => fetchList("footerSocials"), cachedData),
]);

const contact = computed(() => {
  const article = singletons.value?.find((a) => a.code === "footerContact");
  return { label: article?.title ?? "", url: article?.link ?? "" };
});

const address = computed(() => singletons.value?.find((a) => a.code === "footerAddress")?.content ?? "");
const phone = computed(() => singletons.value?.find((a) => a.code === "footerPhone")?.content ?? "");
// `(0)` = zéro de transition national : à retirer avant un indicatif international, sinon le numéro composé est invalide
const phoneHref = computed(() => (phone.value ? `tel:${phone.value.replace(/\(0\)/g, "").replace(/[^\d+]/g, "")}` : ""));

const logos = computed(() =>
  (logosData.value ?? [])
    .filter((a): a is typeof a & { mainImage: { url: string } } => Boolean(a.mainImage?.url))
    .map((a) => ({ name: a.title, image: a.mainImage.url, link: a.link }))
);

const socials = computed(() => (socialsData.value ?? []).map((a) => ({ icon: a.icon, url: a.link, label: a.title })));
</script>

<template>
  <section
    v-if="show"
    :aria-label="t('footer.guestSection.label')"
    class="relative z-1 bg-inverted bg-ocean bg-size-[100%_30rem] bg-position-[center_top_-11rem] rounded-t-2xl px-6 sm:px-10 pt-10 pb-20"
  >
    <div class="grid grid-cols-1 lg:grid-cols-2 items-start gap-10">
      <div class="flex flex-wrap items-center gap-6">
        <component
          :is="logo.link ? ULink : 'span'"
          v-for="logo in logos"
          :key="logo.name"
          :to="logo.link || undefined"
          :target="logo.link ? '_blank' : undefined"
          :rel="logo.link ? 'noopener noreferrer' : undefined"
          class="contents"
        >
          <img
            :src="logo.image"
            :alt="logo.name || t('footer.partnerLogo')"
            class="h-auto w-auto max-h-17.5 max-w-25 object-contain shrink"
          />
        </component>
      </div>

      <div class="flex max-sm:flex-col flex-wrap gap-6">
        <div class="flex flex-col gap-y-6 md:gap-y-12 grow">
          <div class="flex flex-wrap gap-2 md:gap-3">
            <UButton
              v-for="social in socials"
              :key="social.label"
              :to="social.url"
              :icon="social.icon"
              :aria-label="social.label"
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
              square
              class="size-10 rounded-full justify-center"
            />
          </div>

          <div class="flex flex-col gap-y-6 leading-snug">
            <p v-if="address" class="whitespace-pre-line">{{ address }}</p>
            <p v-if="phone">
              <ULink :href="phoneHref">{{ phone }}</ULink>
            </p>
          </div>
        </div>

        <div v-if="contact.url" class="">
          <UButton :to="contact.url" variant="outline" target="_blank" rel="noopener noreferrer" trailing-icon="icon:external">
            {{ contact.label }}
          </UButton>
        </div>
      </div>
    </div>
  </section>
</template>
